// Color palettes for UI customization
// Users can choose from preset palettes or customize individual colors

// Dark mode background options with glass morphism levels
export const darkModeOptions = [
  {
    id: 'black-75',
    name: 'Glass 75%',
    category: 'black',
    background: '#0A0A0C',
    cardBg: '#0D0D0F',
    textHeading: '#FFFFFF',
    textBody: '#E5E5E5',
    textSubheading: '#A3A3A3',
    borderColor: '#1A1A1F',
    glassOpacity: 0.85,
    glassBg: 'rgba(10, 10, 12, 0.85)',
    glassCard: 'rgba(15, 15, 18, 0.80)',
    glassSidebar: 'rgba(8, 8, 10, 0.90)',
    glassHeader: 'rgba(10, 10, 12, 0.88)',
    blurAmount: '20px'
  },
  {
    id: 'black-50',
    name: 'Glass 50%',
    category: 'black',
    background: '#0D0D10',
    cardBg: '#131316',
    textHeading: '#FFFFFF',
    textBody: '#E5E5E5',
    textSubheading: '#A3A3A3',
    borderColor: '#252530',
    glassOpacity: 0.70,
    glassBg: 'rgba(13, 13, 16, 0.70)',
    glassCard: 'rgba(20, 20, 24, 0.65)',
    glassSidebar: 'rgba(10, 10, 12, 0.80)',
    glassHeader: 'rgba(12, 12, 15, 0.75)',
    blurAmount: '16px'
  },
  {
    id: 'black-25',
    name: 'Glass 25%',
    category: 'black',
    background: '#121215',
    cardBg: '#1A1A1E',
    textHeading: '#FFFFFF',
    textBody: '#E5E5E5',
    textSubheading: '#A3A3A3',
    borderColor: '#2A2A35',
    glassOpacity: 0.55,
    glassBg: 'rgba(18, 18, 21, 0.55)',
    glassCard: 'rgba(26, 26, 30, 0.50)',
    glassSidebar: 'rgba(14, 14, 17, 0.65)',
    glassHeader: 'rgba(16, 16, 19, 0.60)',
    blurAmount: '12px'
  },
  {
    id: 'grey-75',
    name: 'Glass 75%',
    category: 'grey',
    background: '#0F1218',
    cardBg: '#161A22',
    textHeading: '#F9FAFB',
    textBody: '#E5E7EB',
    textSubheading: '#9CA3AF',
    borderColor: '#1E2530',
    glassOpacity: 0.85,
    glassBg: 'rgba(15, 18, 24, 0.85)',
    glassCard: 'rgba(22, 26, 34, 0.80)',
    glassSidebar: 'rgba(12, 14, 20, 0.90)',
    glassHeader: 'rgba(14, 17, 22, 0.88)',
    blurAmount: '20px'
  },
  {
    id: 'grey-50',
    name: 'Glass 50%',
    category: 'grey',
    background: '#141820',
    cardBg: '#1C222C',
    textHeading: '#F9FAFB',
    textBody: '#E5E7EB',
    textSubheading: '#9CA3AF',
    borderColor: '#2A3240',
    glassOpacity: 0.70,
    glassBg: 'rgba(20, 24, 32, 0.70)',
    glassCard: 'rgba(28, 34, 44, 0.65)',
    glassSidebar: 'rgba(16, 20, 26, 0.80)',
    glassHeader: 'rgba(18, 22, 28, 0.75)',
    blurAmount: '16px'
  },
  {
    id: 'grey-25',
    name: 'Glass 25%',
    category: 'grey',
    background: '#1A2030',
    cardBg: '#242C3C',
    textHeading: '#F9FAFB',
    textBody: '#E5E7EB',
    textSubheading: '#D1D5DB',
    borderColor: '#3A4455',
    glassOpacity: 0.55,
    glassBg: 'rgba(26, 32, 48, 0.55)',
    glassCard: 'rgba(36, 44, 60, 0.50)',
    glassSidebar: 'rgba(20, 26, 38, 0.65)',
    glassHeader: 'rgba(24, 30, 42, 0.60)',
    blurAmount: '12px'
  }
];

// Get dark mode option by ID
export const getDarkModeOption = (id) => {
  return darkModeOptions.find(opt => opt.id === id) || darkModeOptions[0];
};

// Bright, contrasting colors - energetic and modern
export const brightPalettes = [
  {
    id: 'vibrant-blue',
    name: 'Vibrant Blue',
    colors: {
      sidebar: '#1E40AF',
      header: '#2563EB',
      background: '#F8FAFC',
      textHeading: '#0F172A',
      textBody: '#334155',
      textSubheading: '#64748B',
      accent: '#3B82F6',
      cardBg: '#FFFFFF'
    }
  },
  {
    id: 'sunset-orange',
    name: 'Sunset Orange',
    colors: {
      sidebar: '#C2410C',
      header: '#EA580C',
      background: '#FFF7ED',
      textHeading: '#431407',
      textBody: '#78350F',
      textSubheading: '#A16207',
      accent: '#F97316',
      cardBg: '#FFFFFF'
    }
  },
  {
    id: 'emerald-green',
    name: 'Emerald Green',
    colors: {
      sidebar: '#047857',
      header: '#059669',
      background: '#F0FDF4',
      textHeading: '#14532D',
      textBody: '#166534',
      textSubheading: '#15803D',
      accent: '#10B981',
      cardBg: '#FFFFFF'
    }
  },
  {
    id: 'royal-purple',
    name: 'Royal Purple',
    colors: {
      sidebar: '#6B21A8',
      header: '#7C3AED',
      background: '#FAF5FF',
      textHeading: '#3B0764',
      textBody: '#581C87',
      textSubheading: '#6B21A8',
      accent: '#8B5CF6',
      cardBg: '#FFFFFF'
    }
  },
  {
    id: 'ruby-red',
    name: 'Ruby Red',
    colors: {
      sidebar: '#B91C1C',
      header: '#DC2626',
      background: '#FEF2F2',
      textHeading: '#450A0A',
      textBody: '#7F1D1D',
      textSubheading: '#991B1B',
      accent: '#EF4444',
      cardBg: '#FFFFFF'
    }
  },
  {
    id: 'ocean-teal',
    name: 'Ocean Teal',
    colors: {
      sidebar: '#0F766E',
      header: '#0D9488',
      background: '#F0FDFA',
      textHeading: '#134E4A',
      textBody: '#115E59',
      textSubheading: '#0F766E',
      accent: '#14B8A6',
      cardBg: '#FFFFFF'
    }
  }
];

// Classic, mild colors - professional and elegant
export const classicPalettes = [
  {
    id: 'slate-professional',
    name: 'Slate Professional',
    colors: {
      sidebar: '#334155',
      header: '#475569',
      background: '#F8FAFC',
      textHeading: '#1E293B',
      textBody: '#475569',
      textSubheading: '#64748B',
      accent: '#6366F1',
      cardBg: '#FFFFFF'
    }
  },
  {
    id: 'warm-neutral',
    name: 'Warm Neutral',
    colors: {
      sidebar: '#57534E',
      header: '#78716C',
      background: '#FAFAF9',
      textHeading: '#292524',
      textBody: '#57534E',
      textSubheading: '#78716C',
      accent: '#A855F7',
      cardBg: '#FFFFFF'
    }
  },
  {
    id: 'navy-classic',
    name: 'Navy Classic',
    colors: {
      sidebar: '#1E3A5F',
      header: '#2C4F7C',
      background: '#F5F7FA',
      textHeading: '#0F2744',
      textBody: '#374A5F',
      textSubheading: '#5A7085',
      accent: '#3B82F6',
      cardBg: '#FFFFFF'
    }
  },
  {
    id: 'forest-elegant',
    name: 'Forest Elegant',
    colors: {
      sidebar: '#2D4A3E',
      header: '#3D5E4F',
      background: '#F5F9F7',
      textHeading: '#1A2E25',
      textBody: '#3D5E4F',
      textSubheading: '#5A7A6B',
      accent: '#22C55E',
      cardBg: '#FFFFFF'
    }
  },
  {
    id: 'charcoal-modern',
    name: 'Charcoal Modern',
    colors: {
      sidebar: '#27272A',
      header: '#3F3F46',
      background: '#FAFAFA',
      textHeading: '#18181B',
      textBody: '#3F3F46',
      textSubheading: '#71717A',
      accent: '#F59E0B',
      cardBg: '#FFFFFF'
    }
  },
  {
    id: 'burgundy-refined',
    name: 'Burgundy Refined',
    colors: {
      sidebar: '#5F1B2E',
      header: '#7C2D41',
      background: '#FDF8F9',
      textHeading: '#3D1120',
      textBody: '#5F1B2E',
      textSubheading: '#8B4A5C',
      accent: '#E11D48',
      cardBg: '#FFFFFF'
    }
  }
];

// Dark mode variants for each palette
export const darkModeOverrides = {
  background: '#0F172A',
  cardBg: '#1E293B',
  textHeading: '#F8FAFC',
  textBody: '#CBD5E1',
  textSubheading: '#94A3B8'
};

// Dealership color mappings - extended with all color properties
export const dealershipColorSchemes = {
  'Shell': {
    sidebar: '#B81820',
    header: '#ED1C24',
    background: '#FFFBF5',
    textHeading: '#1A0505',
    textBody: '#4A1010',
    textSubheading: '#7A2020',
    accent: '#FFD500',
    cardBg: '#FFFFFF'
  },
  'Nayara': {
    sidebar: '#004A7C',
    header: '#0072BC',
    background: '#F5FAFF',
    textHeading: '#001A2E',
    textBody: '#003356',
    textSubheading: '#005A94',
    accent: '#00A9E0',
    cardBg: '#FFFFFF'
  },
  'HPCL': {
    sidebar: '#00004A',
    header: '#CE0000',
    background: '#FFF5F5',
    textHeading: '#1A0000',
    textBody: '#4A0000',
    textSubheading: '#7A0000',
    accent: '#F78222',
    cardBg: '#FFFFFF'
  },
  'BPCL': {
    sidebar: '#005080',
    header: '#007BC9',
    background: '#F5FBFF',
    textHeading: '#001A2E',
    textBody: '#003356',
    textSubheading: '#005A94',
    accent: '#FFE000',
    cardBg: '#FFFFFF'
  },
  'IOCL': {
    sidebar: '#010D30',
    header: '#02164F',
    background: '#FFFAF5',
    textHeading: '#010510',
    textBody: '#02164F',
    textSubheading: '#4A5D8F',
    accent: '#F37022',
    cardBg: '#FFFFFF'
  },
  'Jio-BP': {
    sidebar: '#006400',
    header: '#009B00',
    background: '#F5FFF5',
    textHeading: '#001A00',
    textBody: '#004A00',
    textSubheading: '#007800',
    accent: '#AFCA08',
    cardBg: '#FFFFFF'
  },
  'Reliance Petroleum': {
    sidebar: '#004570',
    header: '#0070B4',
    background: '#F5FAFF',
    textHeading: '#001A2E',
    textBody: '#003356',
    textSubheading: '#005A94',
    accent: '#D7AF64',
    cardBg: '#FFFFFF'
  }
};

// Default Fuelogic colors
export const defaultColors = {
  sidebar: '#0040A0',
  header: '#0066FF',
  background: '#F5F8FF',
  textHeading: '#0F172A',
  textBody: '#334155',
  textSubheading: '#64748B',
  accent: '#00CED1',
  cardBg: '#FFFFFF'
};

// Color property labels for UI
export const colorPropertyLabels = {
  sidebar: 'Sidebar',
  header: 'Header Bar',
  background: 'Background',
  textHeading: 'Heading Text',
  textBody: 'Body Text',
  textSubheading: 'Subheading Text',
  accent: 'Accent Color',
  cardBg: 'Card Background'
};

// Get colors for a dealership
export const getColorsForDealership = (dealership) => {
  if (!dealership) return defaultColors;
  
  const nameMap = {
    'Nayra': 'Nayara',
    'Reliance': 'Reliance Petroleum',
    'IOL': 'IOCL',
    'Jio': 'Jio-BP'
  };
  
  const normalizedName = nameMap[dealership] || dealership;
  return dealershipColorSchemes[normalizedName] || defaultColors;
};

// Generate CSS variables from custom colors
export const generateCustomColorCSS = (colors, isDarkMode = false) => {
  const effectiveColors = isDarkMode ? {
    ...colors,
    background: darkModeOverrides.background,
    cardBg: darkModeOverrides.cardBg,
    textHeading: darkModeOverrides.textHeading,
    textBody: darkModeOverrides.textBody,
    textSubheading: darkModeOverrides.textSubheading
  } : colors;
  
  return {
    '--custom-sidebar': effectiveColors.sidebar,
    '--custom-header': effectiveColors.header,
    '--custom-background': effectiveColors.background,
    '--custom-text-heading': effectiveColors.textHeading,
    '--custom-text-body': effectiveColors.textBody,
    '--custom-text-subheading': effectiveColors.textSubheading,
    '--custom-accent': effectiveColors.accent,
    '--custom-card-bg': effectiveColors.cardBg
  };
};
